/**
 * Image helpers for PDF embedding.
 *
 * Rendered floor plans / elevations are captured as lossless PNG data URLs
 * at the DPR-scaled backing-store size (3840×2160 on a Retina display).
 * Embedded as-is they make a single room sheet ~50 MB, which pushes a
 * composed construction document past the 100 MB storage write limit
 * (`web-app/rules/storage.rules.*`, `projects/**`). Line drawings on white
 * lose nothing visible as JPEG and shrink ~20-50×.
 */

export const loadImage = (src) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });

/**
 * Crop a capture to its non-white content (+ `margin` px) and re-encode as
 * JPEG on white. Returns the cropped data URL with its pixel size and the
 * crop fraction of the original, or null if the image is blank/unloadable.
 *
 * Scale stays exact after cropping: pixels per foot are unchanged, so a
 * caller scales its LOGICAL width/height by `fraction` and places as usual.
 * Cropping is what lets the fit pick a larger preferred scale — a 1920 px
 * canvas that is mostly margin no longer has to fit the cell as a whole.
 */
export const trimImageWhitespace = async (
  dataUrl,
  { margin = 8, quality = 0.85, threshold = 250, axis = 'both' } = {}
) => {
  try {
    const canvas = await rasterizeOnWhite(dataUrl);
    const { width, height } = canvas;
    if (!width || !height) return null;
    const ink = findInkBounds(
      canvas.getContext('2d').getImageData(0, 0, width, height),
      threshold
    );
    if (!ink) return null;

    // `axis: 'x'` crops the sides only and keeps the full height, so every
    // capture of the same frame comes back the same height — which is what
    // lets a sheet stand its elevations on ONE ground line. Trimming
    // vertically moves the floor line by however deep that drawing's
    // dimension rows happen to be.
    const trimY = axis !== 'x';
    const top = trimY ? Math.max(0, ink.top - margin) : 0;
    const left = Math.max(0, ink.left - margin);
    const bottom = trimY
      ? Math.min(height - 1, ink.bottom + margin)
      : height - 1;
    const right = Math.min(width - 1, ink.right + margin);
    const w = right - left + 1;
    const h = bottom - top + 1;

    const out = document.createElement('canvas');
    out.width = w;
    out.height = h;
    const octx = out.getContext('2d');
    octx.fillStyle = '#ffffff';
    octx.fillRect(0, 0, w, h);
    octx.drawImage(canvas, left, top, w, h, 0, 0, w, h);
    return {
      dataUrl: out.toDataURL('image/jpeg', quality),
      width: w,
      height: h,
      fraction: { x: w / width, y: h / height },
    };
  } catch (e) {
    console.warn('[pdfImageUtils] trimImageWhitespace failed:', e);
    return null;
  }
};

/** An image drawn onto a white canvas of its own pixel size. */
export const rasterizeOnWhite = async (dataUrl) => {
  const img = await loadImage(dataUrl);
  const canvas = document.createElement('canvas');
  canvas.width = img.width;
  canvas.height = img.height;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(img, 0, 0);
  return canvas;
};

/**
 * The ink on a white capture: the inclusive bounding box of every pixel
 * darker than `threshold` in any channel, or null for a blank image.
 *
 * @param {{data: Uint8ClampedArray, width: number, height: number}} pixels - ImageData
 * @returns {{top, right, bottom, left} | null} px
 */
export const findInkBounds = ({ data, width, height }, threshold = 250) => {
  let top = height,
    bottom = -1,
    left = width,
    right = -1;
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const i = (y * width + x) * 4;
      if (
        data[i] < threshold ||
        data[i + 1] < threshold ||
        data[i + 2] < threshold
      ) {
        if (y < top) top = y;
        if (y > bottom) bottom = y;
        if (x < left) left = x;
        if (x > right) right = x;
      }
    }
  }
  return bottom < top || right < left ? null : { top, right, bottom, left };
};

/**
 * Re-encode an image data URL as JPEG on a white background.
 * Pixel dimensions are unchanged, so any logical-size / pixelsPerFoot
 * bookkeeping for the image stays valid.
 *
 * @param {string} dataUrl - PNG/JPEG data URL
 * @param {number} quality - JPEG quality 0..1
 * @returns {Promise<string>} JPEG data URL
 */
export const toJpegDataUrl = async (dataUrl, quality = 0.85) => {
  if (!dataUrl || dataUrl.startsWith('data:image/jpeg')) return dataUrl;
  const canvas = await rasterizeOnWhite(dataUrl);
  return canvas.toDataURL('image/jpeg', quality);
};

/**
 * The centred crop of a `width` x `height` image that has `aspect` (w / h):
 * full height of a wider image, full width of a taller one. In pixels.
 */
export const coverCropRect = (width, height, aspect) => {
  if (width / height > aspect) {
    const w = Math.round(height * aspect);
    return { x: Math.round((width - w) / 2), y: 0, w, h: height };
  }
  const h = Math.round(width / aspect);
  return { x: 0, y: Math.round((height - h) / 2), w: width, h };
};

/**
 * Crop an image to `aspect` (`coverCropRect`) so it fills a frame of that
 * shape instead of sitting in it with bands of white. JPEG data URL.
 */
export const coverCropToAspect = async (src, aspect, quality = 0.9) => {
  const img = await loadImage(src);
  const r = coverCropRect(img.width, img.height, aspect);
  const canvas = document.createElement('canvas');
  canvas.width = r.w;
  canvas.height = r.h;
  canvas.getContext('2d').drawImage(img, r.x, r.y, r.w, r.h, 0, 0, r.w, r.h);
  return canvas.toDataURL('image/jpeg', quality);
};
